// <s-quick-size> — one-tap add to cart on product cards. Commands: --toggle/--close (size row), --add (from a size button or the icon). Colour comes from the card's pinned swatch.
import { getCartErrorMessage } from '@theme/cart-feedback';

class SQuickSize extends HTMLElement {
  #ac;
  #variants = [];
  #busy = false;

  connectedCallback() {
    try {
      this.#variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('command', (event) => {
      if (event.command === '--toggle') this.#setOpen(!this.hasAttribute('data-open'));
      else if (event.command === '--close') this.#setOpen(false);
      else if (event.command === '--add') this.#add(event.source);
    }, { signal });

    // Refresh which sizes exist in the chosen colour whenever the card is entered or a swatch is picked.
    const card = this.closest('article');
    card?.addEventListener('pointerenter', () => this.#syncSizes(), { signal });
    card?.addEventListener('swatch:change', () => this.#syncSizes(), { signal });
    document.addEventListener('click', (event) => {
      if (this.hasAttribute('data-open') && !card?.contains(event.target)) this.#setOpen(false);
    }, { signal });
    this.#syncSizes();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #setOpen(open) {
    this.toggleAttribute('data-open', open);
    this.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', String(open));
    if (open) {
      this.#syncSizes();
      this.querySelector('[data-size]:not(:disabled)')?.focus({ preventScroll: true });
    }
  }

  #chosenColor() {
    const colorIndex = Number(this.dataset.colorIndex);
    if (colorIndex < 0) return null;
    const pinned = this.closest('article')?.querySelector('s-card-swatches button[aria-pressed="true"]');
    if (pinned?.dataset.color) return pinned.dataset.color;
    const fallback = this.#variants.find((variant) => String(variant.id) === this.dataset.defaultVariant);
    return fallback?.options[colorIndex] ?? null;
  }

  #findVariant(size) {
    const colorIndex = Number(this.dataset.colorIndex);
    const sizeIndex = Number(this.dataset.sizeIndex);
    const color = this.#chosenColor();
    const matches = this.#variants.filter((variant) =>
      (sizeIndex < 0 || variant.options[sizeIndex] === size) &&
      (colorIndex < 0 || color === null || variant.options[colorIndex] === color)
    );
    return matches.find((variant) => variant.available) || null;
  }

  #syncSizes() {
    for (const button of this.querySelectorAll('[data-size]')) {
      button.disabled = !this.#findVariant(button.dataset.size);
    }
  }

  async #add(source) {
    if (this.#busy) return;
    const size = source?.dataset?.size;
    const variant = size !== undefined
      ? this.#findVariant(size)
      : (Number(this.dataset.sizeIndex) < 0 ? this.#findVariant(null) : null);
    if (!variant) return;

    // Without Standard Actions, fall back to the product page with that variant preselected.
    if (!window.Shopify?.actions?.updateCart) {
      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.searchParams.set('variant', variant.id);
      window.location.assign(url);
      return;
    }

    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: String(variant.id), quantity: 1 }] },
        { event: { context: 'product', detail: { source: 's-quick-size' } } }
      );
      const errorMessage = result?.userErrors?.[0]?.message;
      if (errorMessage) {
        this.#announce(errorMessage);
        return;
      }
      this.#announce(this.dataset.addedMessage || '');
      this.#flashAdded();
      this.#setOpen(false);
      await window.Shopify.actions.openCart?.();
    } catch (error) {
      this.#announce(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
    }
  }

  #flashAdded() {
    const cartIcon = this.querySelector('[data-icon-cart]');
    const checkIcon = this.querySelector('[data-icon-check]');
    if (!cartIcon || !checkIcon) return;
    cartIcon.classList.add('hidden');
    checkIcon.classList.remove('hidden');
    checkIcon.classList.add('inline-flex');
    setTimeout(() => {
      checkIcon.classList.add('hidden');
      checkIcon.classList.remove('inline-flex');
      cartIcon.classList.remove('hidden');
    }, 1600);
  }

  #announce(message) {
    const status = this.querySelector('[data-status]');
    if (status) status.textContent = message;
  }
}

if (!customElements.get('s-quick-size')) customElements.define('s-quick-size', SQuickSize);
